// Technical > Portal ↔ Role Matrix
//
// Read-only verification page for the role → portal mapping that drives the
// /portals chooser and portal switchers. Two sources are shown side by side
// in one grid:
//
//   - SERVER: the backend RoleRegistry projection (PortalAccessResolver),
//     fetched from /api/secure/cc/support/technical/portal-role-matrix. This
//     is what ships on LoginData.availablePortals and is the source of truth.
//   - LOCAL: this frontend build's role lists in shared/config/portalConfig.js
//     (the fallback used when no server list is stored).
//
// Cells where the two disagree are highlighted — that drift is exactly the
// class of bug this page exists to catch (e.g. the clinic-owner Control
// Center dead-end tile). When the backend endpoint is unavailable (not yet
// deployed), the grid renders from local config only, with a warning.
import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { PORTAL_CONFIG } from 'src/shared/config/portalConfig'
import { getPortalRoleMatrix } from 'src/supportPortal/services/technicalService'

const CELL_STATES = {
  BOTH: { label: '✓', color: '#1b7f4d', bg: 'rgba(27, 127, 77, 0.12)', title: 'Granted (server + local)' },
  BOTH_PENDING: { label: '◐', color: '#b45309', bg: 'rgba(180, 83, 9, 0.12)', title: 'Granted, approval pending (server + local)' },
  SERVER_ONLY: { label: 'S', color: '#1d4ed8', bg: 'rgba(29, 78, 216, 0.14)', title: 'DRIFT: server grants this portal, local role list does not' },
  SERVER_ONLY_PENDING: { label: 'S◐', color: '#1d4ed8', bg: 'rgba(29, 78, 216, 0.14)', title: 'DRIFT: server grants (pending), local role list does not' },
  LOCAL_ONLY: { label: 'L', color: '#b91c1c', bg: 'rgba(185, 28, 28, 0.14)', title: 'DRIFT: local role list grants this portal, server does not' },
}

function buildLocalGrantsByRole() {
  const grantsByRole = new Map()
  PORTAL_CONFIG.forEach((portal) => {
    portal.roles.forEach((roleId) => {
      if (!grantsByRole.has(roleId)) grantsByRole.set(roleId, new Set())
      grantsByRole.get(roleId).add(portal.id)
    })
  })
  return grantsByRole
}

function resolveCellState({ serverGrant, localGranted, serverAvailable }) {
  if (serverAvailable) {
    if (serverGrant && localGranted) return serverGrant.pending ? CELL_STATES.BOTH_PENDING : CELL_STATES.BOTH
    if (serverGrant) return serverGrant.pending ? CELL_STATES.SERVER_ONLY_PENDING : CELL_STATES.SERVER_ONLY
    if (localGranted) return CELL_STATES.LOCAL_ONLY
    return null
  }
  return localGranted ? CELL_STATES.BOTH : null
}

export default function PortalRoleMatrixPage() {
  const [serverData, setServerData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const resp = await getPortalRoleMatrix()
      if (cancelled) return
      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        setServerData(getDataMapFromCommonResponse(resp))
      } else {
        setLoadError(true)
      }
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const localGrantsByRole = useMemo(buildLocalGrantsByRole, [])
  const portalIds = useMemo(() => PORTAL_CONFIG.map((portal) => portal.id), [])
  const serverAvailable = Boolean(serverData?.roles)

  // Rows: union of server roles and locally-referenced roles, grouped by the
  // server's roleType when known so related roles sit together.
  const rows = useMemo(() => {
    const serverRoles = Array.isArray(serverData?.roles) ? serverData.roles : []
    const byRoleId = new Map(serverRoles.map((role) => [role.roleId, role]))
    const merged = serverRoles.map((role) => ({ ...role, localPortals: localGrantsByRole.get(role.roleId) }))
    Array.from(localGrantsByRole.keys())
      .filter((roleId) => !byRoleId.has(roleId))
      .sort()
      .forEach((roleId) => {
        merged.push({
          roleId,
          roleType: serverAvailable ? 'NOT IN SERVER REGISTRY' : undefined,
          clientPortals: null,
          localPortals: localGrantsByRole.get(roleId),
        })
      })
    return merged
  }, [serverData, localGrantsByRole, serverAvailable])

  const { grantRows, noPortalRows, driftCount } = useMemo(() => {
    const grants = []
    const noPortal = []
    let drift = 0
    rows.forEach((row) => {
      const serverGrantsById = new Map(
        (Array.isArray(row.clientPortals) ? row.clientPortals : []).map((grant) => [grant.portalId, grant]),
      )
      const cells = portalIds.map((portalId) => {
        const state = resolveCellState({
          serverGrant: serverGrantsById.get(portalId),
          localGranted: Boolean(row.localPortals?.has(portalId)),
          serverAvailable,
        })
        if (state === CELL_STATES.SERVER_ONLY || state === CELL_STATES.SERVER_ONLY_PENDING || state === CELL_STATES.LOCAL_ONLY) {
          drift += 1
        }
        return state
      })
      if (cells.some(Boolean)) {
        grants.push({ row, cells })
      } else {
        noPortal.push(row)
      }
    })
    return { grantRows: grants, noPortalRows: noPortal, driftCount: drift }
  }, [rows, portalIds, serverAvailable])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Portal ↔ Role Matrix
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Which roles unlock which portals. SERVER is the backend RoleRegistry projection that ships on
        login (<code>availablePortals</code>); LOCAL is this frontend build&rsquo;s fallback role lists
        (<code>portalConfig.js</code>). Disagreements are highlighted — they are bugs in one of the two.
      </Typography>

      {serverAvailable ? (
        <Alert severity={driftCount > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
          {driftCount > 0
            ? `${driftCount} drift cell(s) between server registry and local config — inspect the S / L markers below.`
            : 'Server registry and local config agree for every role and portal.'}
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {loadError
            ? 'Backend matrix endpoint unavailable (not deployed yet, or access denied) — showing this frontend build’s local config only. Drift detection is off.'
            : 'No server data — showing local config only.'}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {Object.values(CELL_STATES).map((state) => (
          <Chip
            key={state.title}
            size="small"
            label={`${state.label} ${state.title}`}
            sx={{ backgroundColor: state.bg, color: state.color, fontWeight: 600 }}
          />
        ))}
      </Box>

      <TableContainer component={Paper} sx={{ mb: 3, maxHeight: '70vh' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, zIndex: 3, backgroundColor: 'background.paper' }}>
                Role
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              {portalIds.map((portalId) => (
                <TableCell key={portalId} align="center" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {portalId}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {grantRows.map(({ row, cells }) => (
              <TableRow key={row.roleId} hover>
                <TableCell
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    backgroundColor: 'background.paper',
                  }}
                >
                  {row.roleId}
                  {row.requiresApproval ? (
                    <Tooltip title="Approval-pending prospect role">
                      <Chip label="prospect" size="small" sx={{ ml: 1, height: 18, fontSize: 10 }} />
                    </Tooltip>
                  ) : null}
                </TableCell>
                <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{row.roleType || '—'}</TableCell>
                {cells.map((state, index) => (
                  <TableCell key={portalIds[index]} align="center" sx={state ? { backgroundColor: state.bg } : undefined}>
                    {state ? (
                      <Tooltip title={state.title}>
                        <Box component="span" sx={{ color: state.color, fontWeight: 700 }}>
                          {state.label}
                        </Box>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Roles with no portal ({noPortalRows.length})
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Holders of only these roles see no tile on /portals. For Control Center, the registry may map a
        role to the portal, but only roles in CC&rsquo;s own login gate get the tile.
      </Typography>
      <Paper sx={{ p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {noPortalRows.map((row) => (
          <Tooltip
            key={row.roleId}
            title={
              Array.isArray(row.registryPortalIds) && row.registryPortalIds.length > 0
                ? `Registry domain: ${row.registryPortalIds.join(', ')} (no client tile)`
                : 'No portal mapping in the registry'
            }
          >
            <Chip label={row.roleId} size="small" sx={{ fontFamily: 'monospace' }} />
          </Tooltip>
        ))}
        {noPortalRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Every known role grants at least one portal.
          </Typography>
        ) : null}
      </Paper>

      {serverAvailable && Array.isArray(serverData?.controlCenterLoginRoles) ? (
        <>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Control Center login gate
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Mirror of Control Center&rsquo;s ProtectedRoute allowlist — only these roles are ever shown a
            Control Center tile, regardless of registry mappings.
          </Typography>
          <Paper sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {serverData.controlCenterLoginRoles.map((roleId) => (
              <Chip key={roleId} label={roleId} size="small" color="error" variant="outlined" sx={{ fontFamily: 'monospace' }} />
            ))}
          </Paper>
        </>
      ) : null}
    </Box>
  )
}
